import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetDefaultQuestions } from '../../../redux/questionBankSlice';
import { bulkAddIrlRequests, getIrlFolders } from '../../../apiServices/irlScreenApi';
import { WorkstreamOptions } from '../chrome';
import { ListCheckbox, ListNotice } from '../listChrome';
import { stripHtmlText } from './shared';
import {
    buildStockIrlBulkAdd,
    defaultIrlImportWorkstream,
    downloadStockQuestionTemplate,
    formatImportResult,
    parseStockQuestionCsv,
    previewStockQuestionRows,
    selectedStockQuestionRows,
    stockIrlActorFields,
    toggleRowSelection,
    toggleVisibleSelection,
    visibleSelectionState,
} from '../listActions';
import styles from '../irlObservations2026.module.css';

function stockImportError(error) {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim() && !data.trimStart().toLowerCase().startsWith('<!')) {
        return data;
    }
    if (typeof data?.detail === 'string') return data.detail;
    return error?.message || 'The CSV could not be imported.';
}

export default function IRL_Stock_Questions({
    workstreams,
    defaultWorkstreamId,
    busy,
    notice,
    onNotice,
    onBusy,
    onBack,
    onImported,
}) {
    const dispatch = useDispatch();
    const fileRef = React.useRef(null);
    const { selectedProject } = useSelector(state => state.projectData);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const { questionBank } = useSelector(state => state.questionBank);
    const [workstreamId, setWorkstreamId] = React.useState(() => {
        const fallback = defaultIrlImportWorkstream(workstreams, defaultWorkstreamId);
        return fallback?.id != null ? String(fallback.id) : '';
    });
    const [selectedTag, setSelectedTag] = React.useState('');
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [uploadedRows, setUploadedRows] = React.useState([]);

    const selectedWorkstream = (workstreams || []).find(item => String(item.id) === String(workstreamId)) || null;
    const tags = React.useMemo(
        () => [...new Set((questionBank || []).map(item => item.tag).filter(Boolean))],
        [questionBank],
    );
    const catalogQuestions = React.useMemo(
        () => (selectedTag ? (questionBank || []).filter(item => item.tag === selectedTag) : []),
        [questionBank, selectedTag],
    );
    const uploadedPreview = uploadedRows.length > 0;
    const visibleRows = uploadedPreview ? uploadedRows : catalogQuestions;
    const visibleIds = visibleRows.map(item => item.id);
    const selection = visibleSelectionState(visibleIds, selectedIds);

    React.useEffect(() => {
        if (workstreamId) return;
        const fallback = defaultIrlImportWorkstream(workstreams, defaultWorkstreamId);
        if (fallback?.id != null) setWorkstreamId(String(fallback.id));
    }, [defaultWorkstreamId, workstreamId, workstreams]);

    React.useEffect(() => {
        if (!selectedWorkstream?.name) return undefined;
        dispatch(GetDefaultQuestions(selectedWorkstream.name));
        setSelectedTag('');
        setSelectedIds([]);
        return undefined;
    }, [dispatch, selectedWorkstream?.name]);

    const actorFields = stockIrlActorFields({ user, selectedProject, projectUsersAll });

    const submitItems = async (rows, emptyNotice) => {
        if (!selectedWorkstream?.id) {
            onNotice?.('Select a workstream before adding requests.');
            return;
        }
        if (!selectedProject?.vdr?.id) {
            onNotice?.('The requests could not be added.');
            return;
        }
        onBusy?.(true);
        try {
            let folders = [];
            try {
                const folderData = await getIrlFolders(selectedWorkstream.id);
                folders = folderData.folders || [];
            } catch {
                folders = [];
            }
            const { items, skipped } = buildStockIrlBulkAdd(rows, {
                workstreams,
                folders,
                vdrId: selectedProject.vdr.id,
                selectedWorkstream,
                addedBy: actorFields.addedBy,
                addedByOffline: actorFields.addedByOffline,
            });
            if (!items.length) {
                onNotice?.(emptyNotice || formatImportResult({ created: 0, skipped }));
                return;
            }
            const saved = await bulkAddIrlRequests({ items });
            const created = Array.isArray(saved) ? saved.length : items.length;
            onNotice?.(formatImportResult({ created, updated: 0, skipped }));
            await onImported?.();
        } catch (error) {
            onNotice?.(stockImportError(error));
        } finally {
            onBusy?.(false);
        }
    };

    const clearUploaded = () => {
        setUploadedRows([]);
        setSelectedIds([]);
        onNotice?.('');
    };

    const handleUpload = async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
            const rows = previewStockQuestionRows(parseStockQuestionCsv(await file.text()));
            if (!rows.length) {
                onNotice?.('The CSV has no questions to add.');
                return;
            }
            setUploadedRows(rows);
            setSelectedTag('');
            setSelectedIds([]);
            onNotice?.(`${rows.length} question${rows.length === 1 ? '' : 's'} ready. Select rows to add to IRL.`);
        } catch (error) {
            onNotice?.(error?.message || 'The CSV could not be read.');
        }
    };

    const handleAddToIrl = async () => {
        const rows = selectedStockQuestionRows(visibleRows, selectedIds);
        await submitItems(rows, 'Select at least one question.');
    };

    const toggleVisible = () => {
        setSelectedIds(toggleVisibleSelection(visibleIds, selectedIds));
    };

    return (
        <>
            <div className={styles.contentHeader}>
                <div>
                    <h2 className={styles.contentTitle}>Stock Questions</h2>
                    <p className={styles.contentSubtitle}>
                        {uploadedPreview
                            ? 'Select uploaded questions to add to the IRL.'
                            : 'Add catalog questions or a question, tag, response CSV to the IRL.'}
                    </p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={() => fileRef.current?.click()}
                        disabled={busy}>
                        Upload From CSV
                    </button>
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={() => downloadStockQuestionTemplate(selectedProject)}
                        disabled={busy}>
                        Download Template
                    </button>
                    <button type="button" className={styles.outlineBtn} onClick={onBack} disabled={busy}>
                        Back
                    </button>
                </div>
            </div>
            <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className={styles.hiddenFile}
                onChange={handleUpload}
            />
            <div className={styles.stockToolbar}>
                <label className={styles.stockWorkstream}>
                    <span className={styles.visuallyHidden}>Workstream</span>
                    <select
                        className={styles.select}
                        value={workstreamId}
                        aria-label="Workstream"
                        onChange={event => setWorkstreamId(event.target.value)}
                        disabled={busy || !(workstreams || []).length}>
                        <option value="">Select a workstream</option>
                        <WorkstreamOptions workstreams={workstreams} valueKey="id" />
                    </select>
                </label>
            </div>
            <ListNotice message={notice} />
            {selectedWorkstream && !uploadedPreview && tags.length > 0 ? (
                <div className={styles.stockTags}>
                    <p className={styles.stockTagLabel}>Select Tag</p>
                    <div className={styles.stockTagChips}>
                        {tags.map(tag => (
                            <button
                                key={tag}
                                type="button"
                                className={`${styles.chip} ${selectedTag === tag ? styles.chipActive : ''}`}
                                onClick={() => {
                                    setSelectedTag(current => (current === tag ? '' : tag));
                                    setSelectedIds([]);
                                }}>
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}
            {visibleRows.length > 0 ? (
                <>
                    <div className={styles.stockAddRow}>
                        <div className={styles.stockAddLeft}>
                            {uploadedPreview ? (
                                <button
                                    type="button"
                                    className={styles.ghostBtn}
                                    onClick={clearUploaded}
                                    disabled={busy}>
                                    Show stock questions
                                </button>
                            ) : null}
                            <span className={styles.stockSelectedCount}>
                                {selectedIds.length > 0 ? `${selectedIds.length} questions selected` : ''}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={handleAddToIrl}
                            disabled={busy || selectedIds.length === 0 || !selectedWorkstream?.id}>
                            Add to IRL
                        </button>
                    </div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.checkCol}>
                                        <ListCheckbox
                                            appearance="header"
                                            checked={selection.allVisibleSelected}
                                            indeterminate={selection.someVisibleSelected}
                                            onChange={toggleVisible}
                                            label={
                                                uploadedPreview
                                                    ? 'Select all uploaded questions'
                                                    : 'Select all visible stock questions'
                                            }
                                        />
                                    </th>
                                    {uploadedPreview ? (
                                        <>
                                            <th>Question</th>
                                            <th>Tag</th>
                                            <th>Response</th>
                                        </>
                                    ) : (
                                        <th>{stripHtmlText(selectedTag)} Questions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map(item => {
                                    const checked = (selectedIds || []).map(String).includes(String(item.id));
                                    return (
                                        <tr key={item.id}>
                                            <td className={styles.checkCol}>
                                                <ListCheckbox
                                                    checked={checked}
                                                    onChange={() => setSelectedIds(toggleRowSelection(selectedIds, item.id))}
                                                    label={`Select ${stripHtmlText(item.question)}`}
                                                />
                                            </td>
                                            {uploadedPreview ? (
                                                <>
                                                    <td>{stripHtmlText(item.question)}</td>
                                                    <td>{stripHtmlText(item.tag)}</td>
                                                    <td>{stripHtmlText(item.response)}</td>
                                                </>
                                            ) : (
                                                <td>{stripHtmlText(item.question)}</td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}
        </>
    );
}
